import { getAlbumStateBadgeClass } from '@/components/common/ProductUtilities';
import { useTranslation } from 'react-i18next';
import styles from './GroupedProducts.module.scss';
import mapToCommonProduct, { CommonProduct } from './mapToCommonProduct';
import { GroupedProduct } from './useGroupedProducts';
import useRelease from './useRelease';

type Props = {
  albums: GroupedProduct[];
};

export default function GroupedProducts({ albums }: Props) {
  const commonProducts = (albums ?? []).map(mapToCommonProduct);
  return (
    <div>
      <table className={styles['grid']}>
        <TableHeader />
        <TableBody albums={commonProducts} />
      </table>
    </div>
  );
}

function TableHeader() {
  const { t } = useTranslation();
  return (
    <thead>
      <tr>
        <th>{t('product.editProduct.format')}</th>
        <th>
          {t('product.editProduct.title')}{' '}
          <span className={styles['title-version']}>
            ({t('product.editProduct.version')})
          </span>
        </th>
        <th>{t('product.editProduct.packaging')}</th>
        <th>{t('product.editProduct.displayArtist')}</th>
        <th>{t('product.editProduct.releaseDate')}</th>
        <th>{t('product.editProduct.GTIN')}</th>
        <th>{t('product.productSearch.catalogNumber')}</th>
        <th>{t('product.editProduct.primary')}</th>
        <th>{t('product.editProduct.albumState')}</th>
      </tr>
    </thead>
  );
}

function TableBody({ albums }: { albums: CommonProduct[] }) {
  const { t } = useTranslation();
  const primaryProductId = usePrimaryProductId();
  return (
    <tbody>
      {albums.map((album) => {
        return (
          <tr
            key={album.id}
            onDoubleClick={() => {
              if (album.editUrl) {
                window.open(album.editUrl, '_blank');
              }
            }}
          >
            <td>
              <div className="badge format-badge badge-default">
                {album.format}
              </div>
            </td>
            <td>
              {album.title}
              <VersionRenderer version={album.version} />
            </td>
            <td>{album.packaging}</td>
            <td>{album.displayArtist}</td>
            <td>{album.releaseDate}</td>
            <td>{album.gtin}</td>
            <td>{album.catalogNumber}</td>
            <td>{String(album.id?.toString() === primaryProductId)}</td>
            <td>
              {album.state && (
                <div
                  className={`badge state-badge ${getAlbumStateBadgeClass(
                    album.state
                  )}`}
                >
                  {t(`product.${album.state}`)}
                </div>
              )}
            </td>
          </tr>
        );
      })}
    </tbody>
  );
}

function VersionRenderer({ version }: { version?: string | null }) {
  if (!version) return <></>;

  return <span className={styles['title-version']}> ({version})</span>;
}

/**
 * Hook to retrieve the ID of the primary product within the Release to which the current album belongs.
 */
function usePrimaryProductId() {
  const release = useRelease();
  return release?.content
    .find((content) => content.isPrimary)
    ?.urn?.match(/^urn:(prs|cms):([a-zA-Z0-9]+)$/)?.[2];
}
